import {InputBase, Tooltip, Select, ClickAwayListener} from '@mui/material'
import {styled} from '@mui/material/styles'
import PropTypes from 'prop-types'

const PREFIX = 'BreadCrumbComponent'

const classes = {
  root: `${PREFIX}-root`,
  input: `${PREFIX}-input`,
  ribbon: `${PREFIX}-ribbon`,
  filtersWrapperDiv: `${PREFIX}-filtersWrapperDiv`,
  ribbonElement: `${PREFIX}-ribbonElement`,
  ribbonAlpha: `${PREFIX}-ribbonAlpha`,
  label: `${PREFIX}-label`,
  whiteLabel: `${PREFIX}-whiteLabel`,
  purpleLabel: `${PREFIX}-purpleLabel`,
  icon: `${PREFIX}-icon`,
  iconPurple: `${PREFIX}-iconPurple`,
  selector: `${PREFIX}-selector`,
  selectorHandle: `${PREFIX}-selectorHandle`,
  stateName: `${PREFIX}-stateName`,
  timeStampStyle: `${PREFIX}-timeStampStyle`,
  timeStampTextStyle: `${PREFIX}-timeStampTextStyle`,
  timeStampValueStyle: `${PREFIX}-timeStampValueStyle`,
  logoContainer: `${PREFIX}-logoContainer`,
  logo: `${PREFIX}-logo`,
  gutterRight: `${PREFIX}-gutterRight`,
  noStyles: `${PREFIX}-noStyles`
}

const Root = styled('div', {shouldForwardProp: (prop) => prop !== 'sideBarOpen'})(({theme, sideBarOpen}) => ({
  display: 'flex',
  height: '40px',
  alignItems: 'center',
  zIndex: 1202,
  [theme.breakpoints.between(1496, 1628)]: {
    flexWrap: sideBarOpen ? 'wrap' : 'nowrap',
    flexDirection: sideBarOpen ? 'column' : 'row',
    height: 'auto'
  },
  [theme.breakpoints.down(1496)]: {
    flexDirection: 'column',
    height: 'auto'
  },

  [`& .${classes.filtersWrapperDiv}`]: {
    display: 'flex',
    justifyContent: 'flex-end',
    order: 1,
    zIndex: 1222,
    [theme.breakpoints.between(1496, 1628)]: {
      order: sideBarOpen ? 2 : 1,
      width: sideBarOpen ? '100%' : 'fit-content',
      justifyContent: sideBarOpen ? 'center' : 'flex-end'
    },
    [theme.breakpoints.between('md', 1496)]: {
      order: 1,
      width: '100%',
      flexWrap: sideBarOpen ? 'wrap' : 'nowrap',
      justifyContent: sideBarOpen ? 'center' : 'flex-end'
    },
    [theme.breakpoints.down('md')]: {
      flexWrap: 'wrap',
      order: 1,
      width: '100%',
      justifyContent: sideBarOpen ? 'center' : 'flex-end'
    }
  },

  [`& .${classes.ribbonElement}`]: {
    display: 'flex',
    alignItems: 'center',
    height: '40px',
    [theme.breakpoints.down(1496)]: {
      width: '100%',
      order: 2
    },
    [theme.breakpoints.down('sm')]: {
      flexDirection: 'column',
      alignItems: 'flex-start'
    },
    backgroundColor: theme.palette.deloitteBlue5['900'],
    color: theme.palette.common.white,
    position: 'relative',
    fontSize: theme.typography.heading.fontSize.s,
    textDecoration: 'none',
    paddingLeft: theme.spacing(2),
    paddingRight: theme.spacing(2),
    lineHeight: theme.typography.heading.lineHeight.fluffy,
    '&:nth-of-type(1)': {
      zIndex: 4,
      paddingLeft: theme.spacing(2),
      width: '100%'
    },
    '&:nth-of-type(2)': {
      zIndex: 3,
      backgroundColor: theme.palette.deloitteAccessibleBlue['900'],
      whiteSpace: 'nowrap',
      [theme.breakpoints.down('lg')]: {
        paddingLeft: '15px'
      },
      width: '100%'
    }
  },

  [`& .${classes.ribbonAlpha}`]: {
    [theme.breakpoints.down('md')]: {
      display: sideBarOpen && 'flex',
      flexWrap: sideBarOpen && 'wrap'
    },
    [theme.breakpoints.up('md')]: {
      '&::before': {
        borderBottom: '20.5px solid transparent',
        borderLeft: '16.5px solid #fff',
        borderTop: '20.5px solid transparent',
        content: '""',
        height: 0,
        left: 0,
        position: 'absolute',
        top: 0,
        width: 0
      },
      '&::after': {
        borderBottom: '20.5px solid transparent',
        borderLeft: '16.5px solid #fff',
        borderTop: '20.5px solid transparent',
        bottom: 0,
        content: '""',
        height: 0,
        position: 'absolute',
        right: 0,
        top: 0,
        transform: 'translateX(16px)',
        width: 0
      },
      '&:nth-of-type(1)': {
        '&::before': {
          border: 'none'
        },
        '&::after': {
          borderLeftColor: theme.palette.deloitteBlue5['900'],
          [theme.breakpoints.down(1496)]: {
            border: sideBarOpen && 'none'
          }
        }
      },
      '&:nth-of-type(2)': {
        '&::before': {
          borderLeftColor: theme.palette.deloitteBlue5['900']
        },
        '&::after': {
          borderLeftColor: theme.palette.deloitteAccessibleBlue['900'],
          [theme.breakpoints.down(1496)]: {
            border: 'none'
          }
        }
      }
    },
    [theme.breakpoints.only('lg')]: {
      '&:nth-of-type(2)': {
        '&::before': {
          border: 'none'
        },
        '&::after': {
          borderLeftColor: theme.palette.deloitteAccessibleBlue['900']
        }
      }
    },
    [theme.breakpoints.only('md')]: {
      '&:nth-of-type(2)': {
        '&::before': {
          border: 'none'
        },
        '&::after': {
          border: 'none'
        }
      }
    }
  },

  [`& .${classes.label}`]: {
    lineHeight: theme.typography.heading.lineHeight.fluffy,
    fontSize: theme.typography.heading.fontSize.xs,
    fontWeight: theme.typography.heading.fontWeight.regular,
    padding: theme.spacing(0, 1)
  },

  [`& .${classes.whiteLabel}`]: {
    color: theme.palette.common.white
  },

  [`& .${classes.purpleLabel}`]: {
    color: theme.palette.deloitteBlue5['900']
  },

  [`& .${classes.icon}`]: {
    fontSize: theme.typography.heading.fontSize.l,
    color: theme.palette.common.white
  },

  [`& .${classes.selector}`]: {
    display: 'flex',
    fontSize: theme.typography.heading.fontSize.s,
    fontWeight: theme.typography.heading.fontWeight.semibold,
    alignItems: 'center',
    color: theme.palette.common.white,
    lineHeight: theme.typography.heading.lineHeight.fluffy,
    padding: theme.spacing(0, 1),
    '&:hover': {
      cursor: 'pointer'
    }
  },

  [`& .${classes.selectorHandle}`]: {
    paddingLeft: theme.spacing(2),
    paddingRight: 0,
    fontSize: theme.typography.heading.fontSize.l,
    '&:hover': {
      cursor: 'pointer'
    }
  },

  [`& .${classes.stateName}`]: {
    textTransform: 'capitalize'
  },

  [`& .${classes.logoContainer}`]: {
    display: 'flex',
    alignItems: 'center',
    textAlign: 'left',
    minHeight: 40,
    justifyContent: 'flex-start',
    order: 1,
    padding: theme.spacing(0, 2, 0, 2),
    [theme.breakpoints.up('xl')]: {
      minHeight: 'auto',
      alignItems: 'flex-start',
      marginRight: 'auto'
    },
    [theme.breakpoints.between(1496, 1628)]: {
      width: sideBarOpen ? '100%' : 'fit-content',
      justifyContent: sideBarOpen ? 'center' : 'flex-start',
      marginRight: sideBarOpen ? '' : 'auto'
    },
    [theme.breakpoints.between(1280, 1496)]: {
      justifyContent: 'center'
    }
  },

  [`& .${classes.logo}`]: {
    [theme.breakpoints.only('lg')]: {
      maxWidth: '100%'
    },
    maxHeight: 25,
    maxWidth: 135
  },

  [`& .${classes.gutterRight}`]: {
    paddingRight: theme.spacing(3),
    fontSize: theme.typography.heading.fontSize.xs,
    fontWeight: theme.typography.heading.fontWeight.regular
  },

  [`& .${classes.noStyles}`]: {
    margin: 0,
    padding: 0,
    fontSize: '0px',
    fontWeight: '0',
    lineHeight: '0px',
    letterSpacing: '0px'
  }
}))

const SelectInput = styled(InputBase, {shouldForwardProp: (prop) => prop !== 'sideBarOpen'})(
  ({theme, sideBarOpen}) => ({
    '& .MuiInputBase-input': {
      maxWidth: '300px',
      borderRadius: 0,
      position: 'relative',
      backgroundColor: 'transparent',
      border: 'none',
      fontWeight: theme.typography.heading.fontWeight.semibold,
      paddingTop: '7px',
      letterSpacing: '0.5px',
      textTransform: 'capitalize',
      fontSize: theme.typography.heading.fontSize.s,
      color: theme.palette.common.white,
      paddingRight: '8px',
      [theme.breakpoints.down('lg')]: {
        marginLeft: '8px',
        maxWidth: sideBarOpen ? '150px' : '300px'
      },
      '&:focus': {
        border: `1px solid ${theme.palette.common.black}`
      }
    }
  })
)

const ToBeStyledTooltip = ({className, onClickAway = () => {}, ...props}) => (
  <ClickAwayListener onClickAway={onClickAway}>
    <Tooltip {...props} classes={{tooltip: className}} />
  </ClickAwayListener>
)
ToBeStyledTooltip.propTypes = {
  className: PropTypes.any,
  onClickAway: PropTypes.func
}

const StyledTooltip = styled(ToBeStyledTooltip)(({theme}) => ({
  borderRadius: theme.spacing(1),
  lineHeight: theme.typography.body2.lineHeight
}))

const StyledSelect = styled(Select)(() => ({
  height: '35px'
}))

const StyledDiv = styled('div')(({theme}) => ({
  marginTop: '5px',
  padding: theme.spacing(1.25)
}))

const StyledDivHealthPrismLogo = styled('div')(({theme}) => ({
  padding: theme.spacing(1.8)
}))

export {Root, classes, SelectInput, StyledTooltip, StyledSelect, StyledDiv, StyledDivHealthPrismLogo}
